import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Clock, MapPin, PlayCircle } from "lucide-react";
import Header from "@/components/Header";
import ThankYouModal from "@/components/ThankYouModal";
import QrPay from "@/components/QrPay";
import { useLang } from "@/i18n";
import { api, formatApiError, loadRazorpay, track, waLink } from "@/lib/api";
import { toast } from "sonner";

export default function Pay() {
  const { token } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    api.get(`/pay/${token}`).then((r) => { setB(r.data); if (r.data.paid) setPaid(true); }).catch(() => setErr(true));
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/create-order", { booking_id: b.booking_id });
      if (!(await loadRazorpay()) || !window.Razorpay) { toast.error("Could not load Razorpay. Check your connection."); setPaying(false); return; }
      track("payment_start", { booking_id: b.booking_id, source: "pay_link" });
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || data.key_id,
        amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "CHELA", description: "Booking payment",
        prefill: { name: b.name, contact: b.phone, email: b.email || undefined },
        theme: { color: "#E05624" },
        modal: { ondismiss: () => { setPaying(false); toast.error(t("pay_pending")); } },
        handler: async (resp) => {
          try {
            await api.post("/verify-payment", { ...resp, booking_id: b.booking_id });
            track("payment_success", { booking_id: b.booking_id, source: "pay_link" });
            setPaid(true);
          } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Payment verification failed"); } finally { setPaying(false); }
        },
      });
      rzp.on("payment.failed", (x) => { toast.error("Payment failed: " + (x.error?.description || "please try again")); setPaying(false); });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start payment"); setPaying(false); }
  };

  const info = b && { id: b.booking_id, amount: b.amount, when: `${b.date} · ${b.time}`, track: `/track/${token}`, wa: waLink(`Hi CHELA! I just paid for my booking. Ref: ${b.booking_id.slice(-6).toUpperCase()}`) };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      <div className="chela-container max-w-lg py-10">
        {err && <p data-testid="pay-not-found" className="text-center text-[#5A5D65]">{t("paylink_bad")} <Link to="/book" className="underline font-bold text-[#E05624]">{t("book")}</Link></p>}
        {b && (
          <div data-testid="pay-card" className="chela-card rounded-3xl p-6" style={{ transform: "none" }}>
            <h1 className="font-display font-extrabold text-2xl text-[#121316]">{paid ? t("ty_title") : t("paylink_title")}</h1>
            <p className="mt-1 text-[#5A5D65]">{b.name}</p>
            <div className="mt-5 space-y-3 text-[#121316]">
              <div className="flex gap-3"><PlayCircle size={20} className="text-[#E05624]" /> {b.service}</div>
              <div className="flex gap-3"><Clock size={20} className="text-[#E05624]" /> {b.date} · {b.time} · {b.duration}</div>
              <div className="flex gap-3"><MapPin size={20} className="text-[#E05624]" /> {b.location || "-"}</div>
            </div>
            {paid && <div data-testid="pay-done" className="mt-6 rounded-2xl bg-[#E8F7EE] text-[#1B8A4B] font-bold p-4 inline-flex gap-2 w-full justify-center"><CheckCircle2 size={20} /> {t("paylink_paid")}</div>}
            {!paid && b.expired && (
              <div data-testid="pay-expired" className="mt-6 rounded-2xl bg-[#FDECEC] p-4 text-center">
                <p className="font-bold text-[#C0392B]">{t("paylink_expired")}</p>
                <button onClick={() => navigate("/book")} className="mt-3 w-full h-12 rounded-full btn-saffron font-bold">{t("book")}</button>
              </div>
            )}
            {!paid && !b.expired && (
              <>
                <p className="mt-6 text-sm text-center font-semibold text-[#3A3D45]">{t("pay_note")}</p>
                {b.discount > 0 && <p data-testid="pay-offer-applied" className="mt-3 text-sm text-center font-bold text-[#1B8A4B] bg-[#E9F7EF] rounded-xl px-4 py-2.5">{t("offer_applied")} -₹{b.discount}</p>}
                <button data-testid="pay-now" onClick={pay} disabled={paying} className="mt-3 w-full h-14 rounded-full btn-saffron font-bold text-base disabled:opacity-60">{paying ? t("pay_opening") : `${t("pay_now")} ₹${b.amount}`}</button>
                <QrPay bookingId={b.booking_id} t={t} onPaid={() => setPaid(true)} />
              </>
            )}
          </div>
        )}
      </div>
      <ThankYouModal open={paid && !!b && !b.paid} onClose={() => setB((x) => ({ ...x, paid: true }))} t={t} info={info} onHome={() => navigate("/")} />
    </div>
  );
}
